import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { PipelineBoard } from '../components/admin/PipelineBoard';
import { EventsCalendar } from '../components/admin/EventsCalendar';
import { LeadDetail } from '../components/admin/LeadDetail';
import { TodayView } from '../components/admin/TodayView';
import { QuickAddLead } from '../components/admin/QuickAddLead';
import { InboxView } from '../components/admin/InboxView';
import { AutomationsView } from '../components/admin/AutomationsView';
import { AiBrainView } from '../components/admin/AiBrainView';
import { InvoiceBuilder } from '../components/admin/InvoiceBuilder';
import { ContactsView } from '../components/admin/ContactsView';
import { TemplatesView } from '../components/admin/TemplatesView';
import { VendorSheet } from '../components/admin/VendorSheet';
import {
  LogOut, ChevronRight, Clock, CheckCircle, AlertCircle, XCircle, Plus, FileCheck,
  DollarSign, CalendarCheck, Loader2, UserPlus, ScrollText, Save, Pencil, ArrowLeft,
  House, Columns3, MessageSquare, Zap, LayoutGrid, Calendar, Users, FileText, BarChart3, X, Sparkles, Receipt, BookUser,
} from 'lucide-react';

const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };

const STATUS_CONFIG = {
  new: { label: 'New', color: 'text-blue-400 bg-blue-400/10' },
  pending_vendor_review: { label: 'Vendor Review', color: 'text-amber-400 bg-amber-400/10' },
  approved: { label: 'Approved', color: 'text-emerald-400 bg-emerald-400/10' },
  invoice_drafted: { label: 'Invoice Drafted', color: 'text-cyan-400 bg-cyan-400/10' },
  invoice_sent: { label: 'Invoice Sent', color: 'text-white bg-white/10' },
  booked: { label: 'Deposit Paid', color: 'text-orange-400 bg-orange-400/10' },
  paid_in_full: { label: 'Paid in Full', color: 'text-emerald-400 bg-emerald-400/10' },
  completed: { label: 'Completed', color: 'text-zinc-400 bg-white/5' },
  cancelled: { label: 'Cancelled', color: 'text-red-400 bg-red-400/10' },
  pending_approval: { label: 'Pending', color: 'text-amber-400 bg-amber-400/10' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'text-zinc-500 bg-white/5' };
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-md ${cfg.color}`} style={FONT_B} data-testid={`status-${status}`}>
      {cfg.label}
    </span>
  );
}

const REQUEST_STATUS_CLS = {
  approved: 'text-emerald-400 bg-emerald-400/10',
  denied: 'text-red-400 bg-red-400/10',
  pending: 'text-amber-400 bg-amber-400/10',
  expired: 'text-orange-400 bg-orange-400/10',
};

function vendorStatusLabel(v) {
  if (v.active) return 'Active';
  if (v.status === 'rejected') return 'Rejected';
  return 'Inactive';
}

function approvalRateColor(rate) {
  if (rate >= 80) return 'text-emerald-400';
  if (rate >= 50) return 'text-amber-400';
  return 'text-red-400';
}

const VIEW_TITLES = {
  today: 'Today', pipeline: 'Pipeline', inbox: 'Inbox', ai: 'Ask OZ', autos: 'Automations',
  calendar: 'Calendar', proposals: 'Proposals', vendors: 'Vendors', rankings: 'Rankings', contacts: 'Contacts', templates: 'Templates',
};

const DOCK_ITEMS = [
  { key: 'today', label: 'Home', icon: House },
  { key: 'pipeline', label: 'Pipeline', icon: Columns3 },
  { key: 'ai', label: 'OZ', icon: Sparkles, activeCls: 'text-blue-400', isCenter: true },
  { key: 'inbox', label: 'Inbox', icon: MessageSquare },
  { key: 'contacts', label: 'Contacts', icon: BookUser },
];

const MORE_ITEMS = [
  { key: 'autos', label: 'Automations', icon: Zap, desc: 'Text templates & triggers', cls: 'bg-amber-500/10 text-amber-400' },
  { key: 'templates', label: 'Templates', icon: ScrollText, desc: 'Contract & invoice text', cls: 'bg-cyan-500/10 text-cyan-400' },
  { key: 'calendar', label: 'Calendar', icon: Calendar, desc: 'Upcoming & past events', cls: 'bg-blue-500/10 text-blue-400' },
  { key: 'proposals', label: 'Proposals', icon: FileText, desc: 'Every estimate & its status', cls: 'bg-violet-500/10 text-violet-400' },
  { key: 'vendors', label: 'Vendors', icon: Users, desc: 'Approve & manage vendors', cls: 'bg-emerald-500/10 text-emerald-400' },
  { key: 'rankings', label: 'Rankings', icon: BarChart3, desc: 'Vendor performance & priority', cls: 'bg-amber-500/10 text-amber-400' },
];

const VALID_VIEWS = Object.keys(VIEW_TITLES);
const getViewFromHash = () => {
  const h = window.location.hash.replace('#', '');
  return VALID_VIEWS.includes(h) ? h : 'today';
};

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [view, setView] = useState(getViewFromHash);
  const [moreOpen, setMoreOpen] = useState(false);
  const [leadDetailId, setLeadDetailId] = useState(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [vendorFor, setVendorFor] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [proposalDetail, setProposalDetail] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [editingPriorities, setEditingPriorities] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});

  const loadData = useCallback(async () => {
    try {
      const [dashData, propData, vendorData] = await Promise.all([
        api.get('/api/admin/dashboard'),
        api.get('/api/admin/proposals'),
        api.get('/api/admin/vendors'),
      ]);
      setStats(dashData);
      setProposals(propData.proposals);
      setVendors(vendorData.vendors);
    } catch (err) {
      console.error('Failed to load admin dashboard data', err);
      toast.error('Failed to load dashboard data');
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    api.get('/api/config/features').then(f => setDemoMode(!!f.demo_mode)).catch(() => {});
  }, []);

  // Phone back-swipe / browser back: step through tabs & close overlays instead of leaving the app
  useEffect(() => {
    if (!window.location.hash) window.history.replaceState(null, '', '#today');
    const onHash = () => setView(getViewFromHash());
    const onPop = () => {
      setLeadDetailId(null); setQuickAddOpen(false); setInvoiceOpen(false); setMoreOpen(false); setVendorFor(null);
    };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onPop);
    return () => { window.removeEventListener('hashchange', onHash); window.removeEventListener('popstate', onPop); };
  }, []);

  const pushOverlay = () => window.history.pushState({ overlay: true }, '');
  const openLead = (id) => { setLeadDetailId(id); pushOverlay(); };
  const closeOverlay = () => {
    if (window.history.state?.overlay) window.history.back();
    else { setLeadDetailId(null); setQuickAddOpen(false); setInvoiceOpen(false); setMoreOpen(false); setVendorFor(null); }
  };

  const loadRankings = async () => {
    try {
      const data = await api.get('/api/admin/vendor-rankings');
      setRankings(data.rankings);
    } catch (err) {
      console.error('Failed to load vendor rankings', err);
      toast.error('Failed to load vendor rankings');
    }
  };

  const loadProposalDetail = async (proposalId) => {
    try {
      const data = await api.get(`/api/admin/proposals/${proposalId}`);
      setProposalDetail(data);
      setSelectedProposal(proposalId);
    } catch (err) {
      console.error('Failed to load proposal detail', err);
      toast.error('Failed to load proposal details');
    }
  };

  // --- Admin Actions ---
  const startEdit = (proposal) => {
    setEditForm({
      client_name: proposal.client_name || '',
      client_email: proposal.client_email || '',
      client_phone: proposal.client_phone || '',
      event_date: proposal.event_date || '',
      event_time: proposal.event_time || '',
      event_location: proposal.event_location || '',
      event_type: proposal.event_type || '',
      guest_count: proposal.guest_count || '',
      notes: proposal.notes || '',
    });
    setEditMode(true);
  };

  const saveEdit = async (proposalId) => {
    setActionLoading('save_edit');
    try {
      await api.put(`/api/admin/proposals/${proposalId}/edit`, editForm);
      setEditMode(false);
      toast.success('Saved');
      await loadProposalDetail(proposalId);
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setActionLoading(null);
    }
  };

  const updateStatus = async (proposalId, newStatus) => {
    setActionLoading(newStatus);
    try {
      await api.put(`/api/admin/proposals/${proposalId}/status`, { status: newStatus });
      toast.success('Status updated');
      await loadProposalDetail(proposalId);
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const draftInvoice = async (proposalId) => {
    setActionLoading('draft_invoice');
    try {
      await api.post(`/api/admin/proposals/${proposalId}/draft-invoice`);
      toast.success('Invoice drafted');
      await loadProposalDetail(proposalId);
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to draft invoice');
    } finally {
      setActionLoading(null);
    }
  };

  const sendContract = async (proposalId) => {
    if (!window.confirm('Send the Exotic Animal Agreement to this client?')) return;
    setActionLoading('send_contract');
    try {
      await api.post(`/api/admin/proposals/${proposalId}/send-contract`);
      toast.success('Contract sent');
      await loadProposalDetail(proposalId);
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to send contract');
    } finally {
      setActionLoading(null);
    }
  };

  const assignVendors = async (proposalId) => {
    setActionLoading('assign_vendors');
    try {
      const res = await api.post(`/api/admin/proposals/${proposalId}/assign-vendors`);
      toast.success(res.message);
      await loadProposalDetail(proposalId);
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to assign vendors');
    } finally {
      setActionLoading(null);
    }
  };

  const approveVendor = async (vendorId) => {
    setActionLoading(vendorId);
    try {
      await api.post(`/api/admin/vendors/${vendorId}/approve`);
      toast.success('Vendor approved');
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to approve vendor');
    } finally {
      setActionLoading(null);
    }
  };

  const rejectVendor = async (vendorId) => {
    if (!window.confirm('Reject this vendor application?')) return;
    setActionLoading(`reject-${vendorId}`);
    try {
      await api.post(`/api/admin/vendors/${vendorId}/reject`);
      toast.success('Vendor rejected');
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setActionLoading(null);
    }
  };

  const savePriority = async (vendorId) => {
    const priorities = editingPriorities[vendorId];
    if (!priorities) return;
    setActionLoading(`priority-${vendorId}`);
    try {
      await api.put(`/api/admin/vendors/${vendorId}/priorities`, { priority_products: priorities });
      toast.success('Saved');
      await loadRankings();
      setEditingPriorities(prev => { const n = {...prev}; delete n[vendorId]; return n; });
    } catch (err) {
      toast.error(err.message || 'Failed to save priorities');
    } finally {
      setActionLoading(null);
    }
  };

  const updatePriority = (vendorId, animalId, value) => {
    setEditingPriorities(prev => ({
      ...prev,
      [vendorId]: { ...(prev[vendorId] || rankings.find(r => r.vendor_id === vendorId)?.priority_products || {}), [animalId]: parseInt(value) || 99 }
    }));
  };

  const markPayoutPaid = async (requestId) => {
    setActionLoading(`payout-${requestId}`);
    try {
      await api.post(`/api/admin/vendor-requests/${requestId}/payout`, { status: 'paid' });
      toast.success('Payout marked paid');
      await loadProposalDetail(selectedProposal);
    } catch (err) {
      toast.error(err.message || 'Failed to mark payout');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  const switchView = (key) => {
    setMoreOpen(false);
    setSelectedProposal(null);
    setEditMode(false);
    if (window.location.hash !== `#${key}`) window.location.hash = key;
    else setView(key);
    if (key === 'rankings') loadRankings();
  };

  // Action buttons for proposal detail
  const getActions = (proposal) => {
    const s = proposal.status;
    const pid = proposal.proposal_id;
    const actions = [];
    const BTN_VARIANTS = {
      white: 'bg-white text-black',
      green: 'bg-emerald-500/15 text-emerald-400',
      default: 'bg-white/5 text-zinc-400 border border-white/10',
    };
    const btn = (label, icon, fn, key, variant = 'default') => {
      const cls = BTN_VARIANTS[variant] || BTN_VARIANTS.default;
      return (
        <button
          key={key}
          onClick={fn}
          disabled={actionLoading === key}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold rounded-full active:scale-95 transition-transform disabled:opacity-50 ${cls}`}
          style={FONT_B}
          data-testid={`action-${key}`}
        >
          {actionLoading === key ? <Loader2 className="w-3 h-3 animate-spin" /> : icon}
          {label}
        </button>
      );
    };

    if (['new'].includes(s)) {
      actions.push(btn('Send to Vendors', <UserPlus className="w-3 h-3" />, () => assignVendors(pid), 'assign_vendors'));
    }
    if (['new', 'pending_vendor_review'].includes(s)) {
      actions.push(btn('Approve & Send', <CheckCircle className="w-3 h-3" />, () => updateStatus(pid, 'approved'), 'approved', 'green'));
    }
    if (['approved'].includes(s) && !proposal.ghl_invoice_id) {
      actions.push(btn('Draft Invoice', <FileCheck className="w-3 h-3" />, () => draftInvoice(pid), 'draft_invoice', 'white'));
    }
    if (s === 'invoice_sent') {
      // Deposit auto-marks via Stripe/GHL payment webhook — no manual button.
    }
    if (s === 'booked') {
      actions.push(btn('Complete', <CalendarCheck className="w-3 h-3" />, () => updateStatus(pid, 'completed'), 'completed'));
    }
    if (s === 'paid_in_full') {
      actions.push(btn('Complete', <CalendarCheck className="w-3 h-3" />, () => updateStatus(pid, 'completed'), 'completed', 'green'));
    }
    if (!['completed', 'cancelled'].includes(s)) {
      actions.push(btn('Cancel', <XCircle className="w-3 h-3" />, () => {
        if (window.confirm('Cancel this proposal?')) updateStatus(pid, 'cancelled');
      }, 'cancelled'));
    }
    return actions;
  };

  const inputCls = "w-full bg-white/[0.04] border border-white/10 text-white text-sm px-3.5 py-2.5 rounded-xl focus:outline-none focus:border-white/30";

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Glass header */}
      <header className="sticky top-0 z-40 backdrop-blur-2xl bg-black/70 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-1 min-w-0">
            {view !== 'today' && (
              <button onClick={() => switchView('today')} className="w-10 h-10 -ml-3 flex items-center justify-center rounded-full text-zinc-400 active:scale-95 flex-shrink-0 transition-transform" data-testid="header-back-btn" aria-label="Back to Home">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-white text-[22px] font-black tracking-tighter leading-none truncate" style={FONT_H}>{VIEW_TITLES[view]}</h1>
                {demoMode && (
                  <span className="px-2 py-1 rounded-md bg-amber-400/10 text-amber-400 text-[9px] font-black tracking-[0.2em] flex-shrink-0" style={FONT_B} data-testid="demo-badge">DEMO</span>
                )}
              </div>
              <p className="text-zinc-700 text-[8px] uppercase tracking-[0.3em] font-bold mt-0.5" style={FONT_B}>Exotic Animals Miami</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setQuickAddOpen(true); pushOverlay(); }}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white text-black text-xs font-bold active:scale-95 transition-transform"
              style={FONT_B}
              data-testid="quick-add-lead-btn"
            >
              <Plus className="w-3.5 h-3.5" /> New Lead
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-7 pb-36">
        {view === 'today' && <TodayView onOpenLead={openLead} />}
        {view === 'pipeline' && <PipelineBoard onOpenProposal={openLead} />}
        {view === 'inbox' && <InboxView onOpenLead={openLead} />}
        {view === 'ai' && <AiBrainView />}
        {view === 'autos' && <AutomationsView />}
        {view === 'contacts' && <ContactsView onOpenLead={openLead} />}
        {view === 'templates' && <TemplatesView />}
        {view === 'calendar' && <EventsCalendar onOpenLead={openLead} />}

        {/* Proposals list */}
        {view === 'proposals' && !selectedProposal && (
          <div className="space-y-3" data-testid="proposals-list">
            {proposals.length === 0 ? (
              <p className="text-zinc-600 text-center py-16" style={FONT_B}>No proposals yet</p>
            ) : proposals.map(p => (
              <button
                key={p.proposal_id}
                onClick={() => loadProposalDetail(p.proposal_id)}
                className="w-full bg-[#111111] border border-white/5 rounded-2xl px-5 py-4 flex items-center justify-between active:scale-[0.99] transition-transform text-left"
                data-testid={`proposal-${p.proposal_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <span className="text-white font-bold tracking-tight text-[15px] truncate" style={FONT_H}>{p.client_name}</span>
                    <StatusBadge status={p.status} />
                    {(p.ghl_contract_id || p.contract_sent_at) && (
                      <span className="text-[10px] text-emerald-500/80 flex items-center gap-1 font-bold" data-testid={`contract-badge-${p.proposal_id}`}>
                        <ScrollText className="w-3 h-3" /> Contract
                      </span>
                    )}
                  </div>
                  <div className="flex gap-3 text-zinc-600 text-xs" style={FONT_B}>
                    <span>{p.event_type}</span>
                    <span>{p.event_date}</span>
                    <span>{p.items?.length} items</span>
                    <span className="text-zinc-300 font-bold tabular-nums">${p.total?.toLocaleString()}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-700" />
              </button>
            ))}
          </div>
        )}

        {/* Proposal Detail */}
        {view === 'proposals' && selectedProposal && proposalDetail && (
          <div data-testid="proposal-detail">
            <button onClick={() => { setSelectedProposal(null); setEditMode(false); }} className="text-zinc-500 text-sm mb-5 font-bold" style={FONT_B} data-testid="back-to-proposals">
              &larr; Back to proposals
            </button>

            <div className="bg-[#111111] border border-white/5 rounded-2xl p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3 min-w-0">
                  <h2 className="text-white text-xl font-black tracking-tighter truncate" style={FONT_H}>{proposalDetail.proposal.client_name}</h2>
                  <StatusBadge status={proposalDetail.proposal.status} />
                </div>
                {!editMode ? (
                  <button onClick={() => startEdit(proposalDetail.proposal)} className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-full border border-white/10 text-zinc-400 active:scale-95 transition-transform" style={FONT_B} data-testid="edit-proposal-btn">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => setEditMode(false)} className="px-4 py-2 text-xs font-bold rounded-full border border-white/10 text-zinc-500" style={FONT_B}>Cancel</button>
                    <button onClick={() => saveEdit(proposalDetail.proposal.proposal_id)} disabled={actionLoading === 'save_edit'} className="flex items-center gap-1 px-5 py-2 text-xs rounded-full bg-white text-black font-bold active:scale-95 transition-transform disabled:opacity-50" style={FONT_B} data-testid="save-edit-btn">
                      {actionLoading === 'save_edit' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                    </button>
                  </div>
                )}
              </div>

              {/* Client Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 pb-6 border-b border-white/5">
                {editMode ? (
                  <>
                    <input value={editForm.client_name} onChange={(e) => setEditForm({...editForm, client_name: e.target.value})} className={inputCls} data-testid="edit-name" placeholder="Name" />
                    <input type="email" value={editForm.client_email} onChange={(e) => setEditForm({...editForm, client_email: e.target.value})} className={inputCls} data-testid="edit-email" placeholder="Email" />
                    <input type="tel" value={editForm.client_phone} onChange={(e) => setEditForm({...editForm, client_phone: e.target.value})} className={inputCls} data-testid="edit-phone" placeholder="Phone" />
                  </>
                ) : (
                  <>
                    <a href={`mailto:${proposalDetail.proposal.client_email}`} className="text-zinc-400 text-sm truncate" style={FONT_B}>{proposalDetail.proposal.client_email || '—'}</a>
                    <a href={`tel:${proposalDetail.proposal.client_phone}`} className="text-zinc-400 text-sm" style={FONT_B}>{proposalDetail.proposal.client_phone || '—'}</a>
                  </>
                )}
              </div>

              {/* Event Details */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                {[
                  ['Event Type', 'event_type', proposalDetail.proposal.event_type, 'edit-event-type', 'text'],
                  ['Date', 'event_date', proposalDetail.proposal.event_date, 'edit-event-date', 'date'],
                  ['Time', 'event_time', proposalDetail.proposal.event_time, 'edit-event-time', 'text'],
                  ['Location', 'event_location', proposalDetail.proposal.event_location, 'edit-location', 'text'],
                  ['Guests', 'guest_count', proposalDetail.proposal.guest_count, 'edit-guests', 'number'],
                ].map(([label, field, val, testId, type]) => (
                  <div key={field}>
                    <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1.5" style={FONT_B}>{label}</span>
                    {editMode ? (
                      <input type={type} value={editForm[field]} onChange={(e) => setEditForm({...editForm, [field]: type === 'number' ? (parseInt(e.target.value) || '') : e.target.value})} className={inputCls} data-testid={testId} />
                    ) : (
                      <span className="text-zinc-200 text-sm" style={FONT_B}>{val || '—'}</span>
                    )}
                  </div>
                ))}
              </div>

              {editMode && (
                <div className="mb-6">
                  <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1.5" style={FONT_B}>Notes</span>
                  <textarea value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} rows={2} className={`${inputCls} resize-none`} data-testid="edit-notes" placeholder="Internal notes..." />
                </div>
              )}
              {!editMode && proposalDetail.proposal.notes && (
                <div className="mb-6 p-4 bg-white/[0.02] border-l-2 border-white/20 rounded-r-xl">
                  <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1" style={FONT_B}>Notes</span>
                  <p className="text-zinc-400 text-sm" style={FONT_B}>{proposalDetail.proposal.notes}</p>
                </div>
              )}

              {/* Financial Summary */}
              <div className="flex flex-wrap items-center gap-6 mb-6 pb-6 border-b border-white/5">
                <div>
                  <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1" style={FONT_B}>Total</span>
                  <span className="text-white text-[26px] font-black tracking-tighter tabular-nums" style={FONT_H}>${proposalDetail.proposal.total?.toLocaleString()}</span>
                </div>
                {proposalDetail.proposal.delivery_fee > 0 && (
                  <div>
                    <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1" style={FONT_B}>Delivery</span>
                    <span className="text-zinc-300 text-sm tabular-nums" style={FONT_B}>${proposalDetail.proposal.delivery_fee?.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex items-center gap-4 ml-auto text-xs" style={FONT_B}>
                  <span className="text-zinc-600">Contract: {(proposalDetail.proposal.ghl_contract_id || proposalDetail.proposal.contract_sent_at) ? <span className="text-emerald-400 font-bold">Sent</span> : <span className="text-zinc-700">Pending</span>}</span>
                  <span className="text-zinc-600">Invoice: {proposalDetail.proposal.ghl_invoice_id ? <span className="text-white font-bold">{['invoice_sent', 'booked', 'completed'].includes(proposalDetail.proposal.status) ? 'Sent' : 'Drafted'}</span> : <span className="text-zinc-700">None</span>}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2" data-testid="proposal-actions">
                {getActions(proposalDetail.proposal)}
              </div>
            </div>

            {/* Items */}
            <p className="text-zinc-500 text-[11px] uppercase tracking-[0.2em] font-semibold mb-3" style={FONT_B}>Line Items ({proposalDetail.proposal.items?.length})</p>
            <div className="space-y-2.5 mb-7">
              {proposalDetail.proposal.items?.map((item, i) => (
                <div key={`item-${item.name}-${i}`} className="bg-[#111111] border border-white/5 rounded-2xl px-5 py-4 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="text-white text-sm font-bold" style={FONT_B}>{item.name}</span>
                      {item.quantity > 1 && <span className="text-zinc-600 text-xs">x{item.quantity}</span>}
                      {item.hours > 1 && <span className="text-zinc-600 text-xs">{item.hours}hr</span>}
                      <span className="text-zinc-300 text-sm tabular-nums font-bold" style={FONT_B}>${item.price?.toLocaleString()}</span>
                    </div>
                    {item.description && <p className="text-zinc-600 text-xs mt-1" style={FONT_B}>{item.description}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    {item.assigned_vendor_name && <span className="text-zinc-600 text-xs" style={FONT_B}>{item.assigned_vendor_name}</span>}
                    {item.vendor_status === 'approved' && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                    {item.vendor_status === 'pending' && <Clock className="w-4 h-4 text-amber-400" />}
                    {item.vendor_status === 'denied' && <XCircle className="w-4 h-4 text-red-400" />}
                    {item.vendor_status === 'unassigned' && <AlertCircle className="w-4 h-4 text-zinc-700" />}
                  </div>
                </div>
              ))}
            </div>

            {/* Vendor Requests */}
            {proposalDetail.vendor_requests?.length > 0 && (
              <>
                <p className="text-zinc-500 text-[11px] uppercase tracking-[0.2em] font-semibold mb-3" style={FONT_B}>Vendor Requests</p>
                <div className="space-y-2.5">
                  {proposalDetail.vendor_requests.map(r => (
                    <div key={r.request_id} className="bg-[#111111] border border-white/5 rounded-2xl px-5 py-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white text-sm font-bold" style={FONT_B}>{r.vendor_name}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-md ${REQUEST_STATUS_CLS[r.status] || 'text-zinc-500 bg-white/5'}`}>{r.status}</span>
                      </div>
                      <div className="text-zinc-600 text-xs" style={FONT_B}>{r.items?.map(i => i.name).join(', ')}</div>
                      {r.notes && <p className="text-zinc-500 text-xs mt-2 italic" style={FONT_B}>&ldquo;{r.notes}&rdquo;</p>}
                      {r.status === 'approved' && (
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5" data-testid={`payout-row-${r.request_id}`}>
                          <span className="text-zinc-500 text-xs" style={FONT_B}>
                            Payout{r.payout_amount != null ? <span className="text-white font-bold ml-1 tabular-nums">${r.payout_amount.toLocaleString()}</span> : <span className="text-zinc-700 ml-1">auto from base rates</span>}
                          </span>
                          {r.payout_status === 'paid' ? (
                            <span className="text-[10px] uppercase tracking-wide px-3 py-1.5 rounded-full text-emerald-400 bg-emerald-400/10 font-bold" data-testid={`payout-paid-${r.request_id}`}>Paid</span>
                          ) : (
                            <button
                              onClick={() => markPayoutPaid(r.request_id)}
                              disabled={actionLoading === `payout-${r.request_id}`}
                              className="text-xs font-bold px-4 py-2 rounded-full bg-emerald-500/15 text-emerald-400 active:scale-95 transition-transform disabled:opacity-50"
                              style={FONT_B}
                              data-testid={`mark-payout-${r.request_id}`}
                            >
                              {actionLoading === `payout-${r.request_id}` ? 'Saving…' : 'Mark Payout Paid'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Vendors */}
        {view === 'vendors' && (
          <div className="space-y-3" data-testid="vendors-list">
            {stats?.pending_vendors > 0 && (
              <p className="text-amber-400 text-xs font-bold mb-2" style={FONT_B}>{stats.pending_vendors} pending approval</p>
            )}
            {vendors.length === 0 ? (
              <p className="text-zinc-600 text-center py-16" style={FONT_B}>No vendors yet</p>
            ) : vendors.map(v => (
              <div key={v.vendor_id} onClick={() => { setVendorFor(v); pushOverlay(); }}
                   className="bg-[#111111] border border-white/5 rounded-2xl px-5 py-4 cursor-pointer active:scale-[0.99] transition-transform" data-testid={`vendor-${v.vendor_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-white font-bold tracking-tight text-[15px]" style={FONT_H}>{v.name}</span>
                    {v.contact_name && <span className="text-zinc-600 text-xs ml-2" style={FONT_B}>{v.contact_name}</span>}
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {v.status === 'pending_approval' ? (
                      <>
                        <button
                          onClick={() => approveVendor(v.vendor_id)}
                          disabled={actionLoading === v.vendor_id}
                          className="flex items-center gap-1 px-4 py-2 text-xs font-bold rounded-full bg-emerald-500/15 text-emerald-400 active:scale-95 transition-transform disabled:opacity-50"
                          style={FONT_B}
                          data-testid={`approve-vendor-${v.vendor_id}`}
                        >
                          {actionLoading === v.vendor_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                          Approve
                        </button>
                        <button
                          onClick={() => rejectVendor(v.vendor_id)}
                          disabled={actionLoading === `reject-${v.vendor_id}`}
                          className="flex items-center gap-1 px-4 py-2 text-xs font-bold rounded-full bg-red-500/10 text-red-400 active:scale-95 transition-transform disabled:opacity-50"
                          style={FONT_B}
                          data-testid={`reject-vendor-${v.vendor_id}`}
                        >
                          <XCircle className="w-3 h-3" /> Reject
                        </button>
                      </>
                    ) : (
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-md ${v.active ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'}`}>
                        {vendorStatusLabel(v)}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-zinc-600 text-xs mb-2.5" style={FONT_B}>{v.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {v.products?.slice(0, 8).map((prod, i) => {
                    const label = typeof prod === 'string' ? prod : (prod.name || prod.id);
                    return (
                      <span key={`prod-${label}-${i}`} className="text-[11px] text-zinc-500 bg-white/5 px-2.5 py-1 rounded-md" style={FONT_B}>{label}</span>
                    );
                  })}
                  {v.products?.length > 8 && <span className="text-[11px] text-zinc-400 font-bold">+{v.products.length - 8} more</span>}
                </div>
                <p className="text-zinc-700 text-[10px] mt-2.5 flex items-center gap-1 font-bold uppercase tracking-wider" style={FONT_B}>Tap to view & edit <ChevronRight className="w-3 h-3" /></p>
              </div>
            ))}
          </div>
        )}

        {/* Rankings */}
        {view === 'rankings' && (
          <div className="space-y-4" data-testid="rankings-tab">
            {rankings.length === 0 ? (
              <p className="text-zinc-600 text-center py-16" style={FONT_B}>No active vendors with products</p>
            ) : rankings.map(v => {
              const animals = v.products || [];
              const currentPriorities = editingPriorities[v.vendor_id] || v.priority_products || {};
              const hasEdits = !!editingPriorities[v.vendor_id];

              return (
                <div key={v.vendor_id} className="bg-[#111111] border border-white/5 rounded-2xl px-5 py-4" data-testid={`ranking-${v.vendor_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-white font-bold tracking-tight text-[15px]" style={FONT_H}>{v.name}</span>
                      {v.contact_name && <span className="text-zinc-600 text-xs ml-2" style={FONT_B}>{v.contact_name}</span>}
                    </div>
                    {hasEdits && (
                      <button
                        onClick={() => savePriority(v.vendor_id)}
                        disabled={actionLoading === `priority-${v.vendor_id}`}
                        className="flex items-center gap-1 px-4 py-2 text-xs font-bold rounded-full bg-white text-black active:scale-95 transition-transform disabled:opacity-50"
                        style={FONT_B}
                        data-testid={`save-priority-${v.vendor_id}`}
                      >
                        {actionLoading === `priority-${v.vendor_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        Save
                      </button>
                    )}
                  </div>

                  <div className="flex gap-4 mb-3 text-xs" style={FONT_B}>
                    {v.stats.approval_rate !== null && (
                      <span className="text-zinc-600">Approval: <span className={approvalRateColor(v.stats.approval_rate)}>{v.stats.approval_rate}%</span></span>
                    )}
                    {v.stats.avg_response_minutes !== null && (
                      <span className="text-zinc-600">Avg Response: <span className="text-zinc-400">{v.stats.avg_response_minutes < 60 ? `${v.stats.avg_response_minutes}m` : `${(v.stats.avg_response_minutes / 60).toFixed(1)}h`}</span></span>
                    )}
                    <span className="text-zinc-600">Requests: <span className="text-zinc-400">{v.stats.total_requests}</span></span>
                    {v.stats.expired > 0 && (
                      <span className="text-zinc-600">Expired: <span className="text-red-400">{v.stats.expired}</span></span>
                    )}
                  </div>

                  {animals.length === 0 ? (
                    <p className="text-zinc-700 text-xs" style={FONT_B}>No animals configured — vendor needs to set up profile</p>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex items-center gap-3 text-[10px] text-zinc-600 uppercase tracking-[0.15em] font-semibold px-1 mb-1" style={FONT_B}>
                        <span className="flex-1">Animal</span>
                        <span className="w-16 text-center">Base Rate</span>
                        <span className="w-20 text-center">Priority</span>
                      </div>
                      {animals.map(a => {
                        const animalId = typeof a === 'string' ? a : a.id;
                        const animalName = typeof a === 'string' ? a : a.name;
                        const baseRate = typeof a === 'object' ? a.baseRate : null;
                        const priority = currentPriorities[animalId] ?? 99;
                        return (
                          <div key={animalId} className="flex items-center gap-3 bg-white/[0.02] px-3 py-2 rounded-xl" data-testid={`priority-${v.vendor_id}-${animalId}`}>
                            <span className="flex-1 text-zinc-300 text-xs truncate" style={FONT_B}>{animalName}</span>
                            {baseRate !== null && <span className="w-16 text-center text-zinc-500 text-xs tabular-nums" style={FONT_B}>${baseRate}</span>}
                            <input
                              type="number"
                              min="1" max="99"
                              value={priority}
                              onChange={(e) => updatePriority(v.vendor_id, animalId, e.target.value)}
                              className="w-20 bg-white/[0.04] border border-white/10 text-white text-xs text-center px-2 py-2 rounded-lg focus:outline-none focus:border-white/30"
                              data-testid={`priority-input-${v.vendor_id}-${animalId}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Floating bottom dock */}
      <nav className="fixed bottom-5 left-4 right-4 z-40 max-w-md mx-auto h-[68px] rounded-full backdrop-blur-2xl bg-black/70 border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.7)] flex items-center justify-around px-2" data-testid="admin-dock">
        {DOCK_ITEMS.map(item => {
          const active = view === item.key;
          if (item.isCenter) {
            return (
              <button key={item.key} onClick={() => switchView(item.key)}
                      className="flex flex-col items-center gap-0.5 flex-1 -mt-6 active:scale-90 transition-transform"
                      data-testid={`dock-${item.key}`} aria-label={item.label}>
                <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-[0_8px_30px_rgba(59,130,246,0.45)] border ${active ? 'bg-blue-500 border-blue-300' : 'bg-gradient-to-br from-blue-500 to-violet-600 border-white/20'}`}>
                  <item.icon className="w-6 h-6 text-white" strokeWidth={2.2} />
                </div>
                <span className={`text-[9px] font-bold tracking-wide ${active ? 'text-blue-400' : 'text-zinc-500'}`} style={FONT_B}>{item.label}</span>
              </button>
            );
          }
          return (
            <button key={item.key} onClick={() => switchView(item.key)}
                    className="flex flex-col items-center gap-1 flex-1 py-2 active:scale-90 transition-transform"
                    data-testid={`dock-${item.key}`} aria-label={item.label}>
              <item.icon className={`w-[22px] h-[22px] ${active ? (item.activeCls || 'text-white') : 'text-zinc-600'}`} strokeWidth={active ? 2.2 : 1.6} />
              <span className={`text-[9px] font-bold tracking-wide ${active ? (item.activeCls || 'text-white') : 'text-zinc-700'}`} style={FONT_B}>{item.label}</span>
            </button>
          );
        })}
        <button onClick={() => { setMoreOpen(true); pushOverlay(); }}
                className="flex flex-col items-center gap-1 flex-1 py-2 active:scale-90 transition-transform"
                data-testid="dock-more" aria-label="More">
          <LayoutGrid className={`w-[22px] h-[22px] ${['autos', 'templates', 'calendar', 'proposals', 'vendors', 'rankings'].includes(view) ? 'text-white' : 'text-zinc-600'}`} strokeWidth={1.6} />
          <span className={`text-[9px] font-bold tracking-wide ${['autos', 'templates', 'calendar', 'proposals', 'vendors', 'rankings'].includes(view) ? 'text-white' : 'text-zinc-700'}`} style={FONT_B}>More</span>
        </button>
      </nav>

      {/* More sheet */}
      <AnimatePresence>
        {moreOpen && (
          <motion.div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeOverlay}>
            <motion.div
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="absolute bottom-0 left-0 right-0 bg-[#0A0A0A] border-t border-white/10 rounded-t-3xl shadow-[0_-10px_40px_rgba(0,0,0,0.8)] px-5 pt-5 pb-10 max-w-lg mx-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="more-sheet"
            >
              <div className="w-10 h-1 rounded-full bg-white/15 mx-auto mb-6" />
              <div className="space-y-2">
                <button onClick={() => { setMoreOpen(false); setInvoiceOpen(true); }}
                        className="w-full flex items-center gap-4 bg-[#111111] border border-white/5 rounded-2xl px-5 py-4 active:scale-[0.98] transition-transform text-left"
                        data-testid="more-new-invoice">
                  <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <Receipt className="w-5 h-5 text-black" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-[15px] font-bold tracking-tight" style={FONT_H}>New Invoice</p>
                    <p className="text-zinc-600 text-xs" style={FONT_B}>Build & text an invoice to anyone</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-700 ml-auto" />
                </button>
                {MORE_ITEMS.map(item => (
                  <button key={item.key} onClick={() => switchView(item.key)}
                          className="w-full flex items-center gap-4 bg-[#111111] border border-white/5 rounded-2xl px-5 py-4 active:scale-[0.98] transition-transform text-left"
                          data-testid={`more-${item.key}`}>
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${item.cls}`}>
                      <item.icon className="w-5 h-5" strokeWidth={1.6} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-[15px] font-bold tracking-tight" style={FONT_H}>{item.label}</p>
                      <p className="text-zinc-600 text-xs" style={FONT_B}>{item.desc}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-700 ml-auto" />
                  </button>
                ))}
                <button onClick={handleLogout}
                        className="w-full flex items-center gap-4 px-5 py-4 active:scale-[0.98] transition-transform text-left"
                        data-testid="logout-btn">
                  <div className="w-11 h-11 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <LogOut className="w-5 h-5 text-red-400" strokeWidth={1.6} />
                  </div>
                  <p className="text-red-400 text-[15px] font-bold tracking-tight" style={FONT_H}>Log out</p>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {leadDetailId && <LeadDetail proposalId={leadDetailId} onClose={closeOverlay} />}
      {vendorFor && <VendorSheet vendor={vendorFor} onClose={closeOverlay} onSaved={loadData} />}
      {invoiceOpen && (
        <InvoiceBuilder
          onClose={() => { closeOverlay(); loadData(); }}
          onSaved={(pid) => { setInvoiceOpen(false); setLeadDetailId(pid); loadData(); }}
        />
      )}
      {quickAddOpen && (
        <QuickAddLead
          onClose={closeOverlay}
          onCreated={(id) => { setQuickAddOpen(false); switchView('pipeline'); setLeadDetailId(id); loadData(); }}
        />
      )}
    </div>
  );
}
